import { Controller } from "@hotwired/stimulus"
import { getJSON, sendJSON, errorDetail } from "../api.js"

// Przełącznik aktywnej organizacji w nawigacji. Widoczny tylko przy > 1 organizacji.
// GET /api/acting-org/ → {acting:{id,name}|null, organizations:[{id,name}]}
// POST /api/acting-org/ {organization:<id>} → 200 + reload | 403 + cofnięcie + komunikat.
// #6b-2: JS-built <select> replaced by a keyboard-accessible menu button + dropdown list
// (role="menu", items role="menuitemradio"). No innerHTML — all nodes by createElement+textContent.
export default class extends Controller {
  static values = { url: String }

  async connect() {
    let data
    try {
      data = await getJSON(this.urlValue)
    } catch {
      return
    }
    if (!data.organizations || data.organizations.length <= 1) return

    this._orgs = data.organizations
    this._actingId = data.acting ? String(data.acting.id) : null
    this._menuOpen = false

    this._btn = this.#buildBtn(data.acting?.name ?? "Organizacja")
    this._menu = this.#buildMenu(data.organizations, this._actingId)
    this._errorEl = document.createElement("span")
    this._errorEl.className = "ls-field-error"
    this._errorEl.hidden = true

    const wrapper = document.createElement("div")
    wrapper.className = "ls-org-menu"
    wrapper.appendChild(this._btn)
    wrapper.appendChild(this._menu)
    wrapper.appendChild(this._errorEl)
    this._wrapper = wrapper

    this._btn.addEventListener("click", (e) => { e.stopPropagation(); this.#toggle() })
    this._btn.addEventListener("keydown", (e) => this.#btnKeydown(e))
    this._menu.addEventListener("keydown", (e) => this.#menuKeydown(e))

    this._outsideClick = (e) => {
      if (!this._wrapper.contains(e.target)) this.#close()
    }
    document.addEventListener("click", this._outsideClick)

    this.element.appendChild(wrapper)
    this.element.hidden = false
  }

  disconnect() {
    document.removeEventListener("click", this._outsideClick)
    this._outsideClick = null
  }

  // --- private ---

  #buildBtn(label) {
    const btn = document.createElement("button")
    btn.type = "button"
    btn.className = "ls-org-menu__btn ls-nav__link"
    btn.setAttribute("aria-haspopup", "menu")
    btn.setAttribute("aria-expanded", "false")
    const nameSpan = document.createElement("span")
    nameSpan.className = "ls-org-menu__btn-name"
    nameSpan.textContent = label
    const caret = document.createElement("span")
    caret.className = "ls-org-menu__caret"
    caret.setAttribute("aria-hidden", "true")
    caret.textContent = "▾"
    btn.appendChild(nameSpan)
    btn.appendChild(caret)
    return btn
  }

  #buildMenu(orgs, actingId) {
    const list = document.createElement("ul")
    list.className = "ls-org-menu__list"
    list.setAttribute("role", "menu")
    list.hidden = true
    for (const org of orgs) {
      const item = document.createElement("li")
      item.setAttribute("role", "presentation")
      const itemBtn = document.createElement("button")
      itemBtn.type = "button"
      itemBtn.className = "ls-org-menu__item"
      itemBtn.setAttribute("role", "menuitemradio")
      const checked = String(org.id) === String(actingId)
      itemBtn.setAttribute("aria-checked", String(checked))
      itemBtn.dataset.orgId = String(org.id)
      itemBtn.textContent = org.name
      itemBtn.addEventListener("click", () => this.#pick(String(org.id)))
      item.appendChild(itemBtn)
      list.appendChild(item)
    }
    return list
  }

  #toggle() {
    if (this._menuOpen) this.#close()
    else this.#open()
  }

  #open() {
    this._menuOpen = true
    this._menu.hidden = false
    this._btn.setAttribute("aria-expanded", "true")
    // Focus first item.
    const first = this._menu.querySelector('[role="menuitemradio"]')
    if (first) first.focus()
  }

  #close() {
    this._menuOpen = false
    this._menu.hidden = true
    this._btn.setAttribute("aria-expanded", "false")
  }

  #btnKeydown(e) {
    if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
      e.preventDefault()
      this.#open()
    }
  }

  #menuKeydown(e) {
    const items = [...this._menu.querySelectorAll('[role="menuitemradio"]')]
    const focused = document.activeElement
    const idx = items.indexOf(focused)
    if (e.key === "ArrowDown") {
      e.preventDefault()
      const next = items[idx + 1] ?? items[0]
      next?.focus()
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      const prev = items[idx - 1] ?? items[items.length - 1]
      prev?.focus()
    } else if (e.key === "Escape") {
      e.preventDefault()
      this.#close()
      this._btn.focus()
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault()
      if (focused?.dataset?.orgId) this.#pick(focused.dataset.orgId)
    }
  }

  async #pick(orgId) {
    this.#close()
    if (orgId === this._actingId) return
    const previousId = this._actingId
    const res = await sendJSON("POST", this.urlValue, { organization: Number(orgId) })
    if (res.ok) {
      this.#clearError()
      this._reload()
    } else {
      // Revert: re-mark previously active item.
      this.#setChecked(this._actingId)
      this.#showError(errorDetail(res.data, "Nie udało się zmienić organizacji."))
    }
  }

  #setChecked(orgId) {
    for (const item of this._menu.querySelectorAll('[role="menuitemradio"]')) {
      item.setAttribute("aria-checked", String(item.dataset.orgId === orgId))
    }
  }

  // Seam jawny — testy mogą nadpisać instancyjnie.
  _reload() {
    window.location.reload()
  }

  #showError(msg) {
    if (!this._errorEl) return
    this._errorEl.textContent = msg
    this._errorEl.hidden = false
  }

  #clearError() {
    if (!this._errorEl) return
    this._errorEl.hidden = true
    this._errorEl.textContent = ""
  }
}
